import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { type ClassValue, clsx } from "clsx";
import * as React from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const buttonVariants = cva(
  "inline-flex items-center justify-center border rounded-sm whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-orange-300 text-orange-950 border-orange-950 shadow-btn hover:shadow-btn-hover transition-all hover:bg-orange-400 active:bg-orange-400 active:shadow-btn-active",
        destructive:
          "bg-red-300 text-red-950 border-red-950 shadow-btn hover:shadow-btn-hover transition-all hover:bg-red-400 active:bg-red-400 active:shadow-btn-active",
        outline:
          "bg-background text-foreground border-border-contrast shadow-btn hover:shadow-btn-hover transition-all hover:bg-ghost active:bg-ghost active:shadow-btn-active",
        secondary:
          "bg-slate-200 text-slate-900 border-slate-950 shadow-btn hover:shadow-btn-hover transition-all hover:bg-slate-400 active:bg-slate-400 active:shadow-btn-active",
        ghost:
          "text-foreground border-transparent transition-all hover:bg-ghost active:bg-ghost",
        link: "underline-offset-4 hover:text-slate-700 underline border-transparent hover:decoration-2 disabled:line-through",
        invisible:
          "bg-transparent text-forground border-transparent border-0 border-b rounded-none hover:border-border-contrast",
      },
      size: {
        default: "px-3 py-1.5 h-10",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8",
        icon: "h-6 w-6",
        link: "h-fit px-0.5 py-0.5 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
